import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

import { Button, Chip, Dropdown, Icon, Input, Modal, ModalSize } from '@column/column-ui-kit';

import { ROUTE } from '~/app/routes';
import { EmptyState, Loading, PageHeader } from '~/components';
import { NotificationList } from '~/components/NotificationList';
import { RenderFields } from '~/components/RenderFields';
import { RelativeTime } from '~/elements';
import { useNavigate } from '~/lib/navigation';
import { CardProgram, CardProgramRepository } from '~/repositories/CardProgramRepository';
import { CreateCardProgramRequest, SimulateRepository } from '~/repositories/SimulateRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

const TableContainer = styled.div`
  padding: 0 var(--page-margin);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(100)};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: ${({ theme }) => theme.foreground};
  border-bottom: 1px solid ${({ theme }) => theme.secondary.blendToBackground(100)};
`;

const Tr = styled.tr`
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.secondary.blendToBackground(50)};
  }
`;

const StatusChip = styled(Chip)<{ $status: string }>`
  text-transform: capitalize;
`;

const NetworkChip = styled(Chip)`
  text-transform: capitalize;
`;

const TypeChip = styled(Chip)`
  text-transform: capitalize;
`;

interface CardProgramForm {
  type: CreateCardProgramRequest['type'];
  description: string;
}

const formatNetworkName = (scheme: string): string => {
  if (!scheme) return '-';
  return scheme.toLowerCase().replace(/_/g, ' ');
};

const formatCardProgramType = (type: string): string => {
  if (!type) return '-';
  return type.toLowerCase().replace(/_/g, ' ');
};

const formatStatus = (status: string): string => {
  if (!status) return '-';
  return status.toLowerCase().replace(/_/g, ' ');
};

const CARD_PROGRAM_TYPE_OPTIONS = [
  { label: 'Debit', value: 'debit' },
  { label: 'Credit', value: 'credit' },
  { label: 'Charge', value: 'charge' },
];

export const PageCardProgramsOverview: React.FC = () => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const [cardPrograms, setCardPrograms] = useState<CardProgram[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formMethods = useForm<CardProgramForm>({
    defaultValues: {
      type: 'debit',
      description: '',
    },
  });

  const fetchPrograms = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await CardProgramRepository.listCardPrograms();
      setCardPrograms(response.cardPrograms || []);
    } catch (err) {
      console.error('Error fetching card programs:', err);
      setError('Failed to load card programs');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrograms();
  }, [currentPlatform?.id, isSandbox, fetchPrograms]);

  const handleOpenModal = useCallback(() => {
    formMethods.reset({ type: 'debit', description: '' });
    setIsModalOpen(true);
  }, [formMethods]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const onSubmit = useCallback(
    async (data: CardProgramForm) => {
      if (isSubmitting) return;
      setIsSubmitting(true);

      try {
        const request: CreateCardProgramRequest = { type: data.type };
        if (data.description.trim()) {
          request.description = data.description.trim();
        }

        await SimulateRepository.createCardProgram(request);
        handleCloseModal();
        addSuccessNotification({ content: 'Card program created' });
        fetchPrograms();
      } catch (err: any) {
        addDangerNotification({ content: err?.message || 'Failed to create card program', display: 'popup' });
      } finally {
        setIsSubmitting(false);
      }
    },
    [isSubmitting, handleCloseModal, addSuccessNotification, addDangerNotification, fetchPrograms]
  );

  const renderTable = () => {
    if (cardPrograms.length === 0) {
      return <EmptyState headline="No card programs" text="No card programs found." />;
    }

    return (
      <Table>
        <thead>
          <tr>
            <Th>Name</Th>
            <Th>Network</Th>
            <Th>BIN</Th>
            <Th>Type</Th>
            <Th>Status</Th>
            <Th>Created</Th>
          </tr>
        </thead>
        <tbody>
          {cardPrograms.map((program) => (
            <Tr key={program.id} onClick={() => navigate(`${ROUTE.CARD_PROGRAMS}/edit/${program.id}`)}>
              <Td>{program.description || program.id}</Td>
              <Td>
                <NetworkChip>{formatNetworkName(program.primaryCardScheme)}</NetworkChip>
              </Td>
              <Td>{program.bin || '-'}</Td>
              <Td>
                <TypeChip>{formatCardProgramType(program.cardProgramType)}</TypeChip>
              </Td>
              <Td>
                <StatusChip $status={program.status}>{formatStatus(program.status)}</StatusChip>
              </Td>
              <Td>{program.createdAt ? <RelativeTime timestamp={program.createdAt} /> : '-'}</Td>
            </Tr>
          ))}
        </tbody>
      </Table>
    );
  };

  return (
    <>
      <Modal isOpen={isModalOpen} onClose={handleCloseModal} size={ModalSize.Small}>
        <Modal.IconCircle icon="AnimationPiggy" type="primary" />
        <FormProvider {...formMethods}>
          <form onSubmit={formMethods.handleSubmit(onSubmit)}>
            <Modal.Content beforeContent={<NotificationList display="popup" variant="light" />}>
              <Modal.Headline align="center">Create Card Program</Modal.Headline>
              <Modal.Paragraph align="center">Create a sandbox Visa card program</Modal.Paragraph>
              {RenderFields<CardProgramForm>({
                sections: [
                  {
                    variant: 'isCompact',
                    fields: [
                      {
                        id: 'type',
                        label: 'Type',
                        children: ({ value, onChange }) => (
                          <Dropdown
                            active={String(value)}
                            onChange={onChange}
                            options={CARD_PROGRAM_TYPE_OPTIONS}
                            fullWidth
                          />
                        ),
                      },
                      {
                        id: 'description',
                        label: 'Description',
                        children: ({ value, onChange }) => (
                          <Input value={String(value)} onChange={onChange} placeholder="Description (optional)" />
                        ),
                      },
                    ],
                  },
                ],
              })}
            </Modal.Content>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal} size="small" type="button">
                Cancel
              </Button>
              <Button isLoading={isSubmitting} isDisabled={isSubmitting} size="small" type="submit">
                Create Program
              </Button>
            </Modal.Footer>
          </form>
        </FormProvider>
      </Modal>

      <PageHeader text="Card Programs" border={false}>
        {isSandbox && (
          <Button onClick={handleOpenModal} size="small" icon={<Icon.Sandbox />}>
            Create Program
          </Button>
        )}
      </PageHeader>
      <Loading isLoading={isLoading}>
        {error ? <EmptyState headline="Error" text={error} /> : <TableContainer>{renderTable()}</TableContainer>}
      </Loading>
    </>
  );
};
